import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation, DOCUMENT } from '@angular/core';
import { GuardsCheckEnd, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { DateAdapter } from '@angular/material/core';
import { Subscription } from 'rxjs';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppStateService } from 'core/app-state/app-state.service';
import { AuthenticationService } from 'core/authentication/authentication.service';
import { NotificationService } from 'core/notification/notification.service';
import { SessionService } from 'core/session/session.service';
import { MatDialog } from '@angular/material/dialog';
import { TouchpointNotificationService } from 'core/touchpoint-notification/touchpoint-notification.service';
import { DataLayerService } from 'core/data-layer-service/data-layer.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { DynamicCssService } from 'core/dynamic-css/dynamic-css.service';
import { DS_ROUTES_CONFIG } from 'core/dynamic-css/ds-routes.config';
import { environment } from '../environments/environment';
import { PaymentChaseComponent } from './modules/pos/quotes/payment-chase/payment-chase.component';
import { OfflineService } from './modules/shared/offline-service';
import { AppLocalization } from './modules/shared/entities/app-localization';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'CRMClient';
  text = '';
  data = {};
  durationInSeconds = 5;
  appStrings: AppLocalization;
  isAuthenticated = false;
  shouldCheckHomePage = true;
  shouldDisplayOfflineTimer = false;
  isOnline = true;
  isMobile = false;
  subs = new Subscription();
  isOnlineWithoutInternet = this.isOnline && !this.appStateService.state.isInternetConnected;

  constructor(
    private notificationService: NotificationService,
    private sessionService: SessionService,
    private appStateService: AppStateService,
    private appInfoService: AppInfoService,
    private authenticationService: AuthenticationService,
    private router: Router,
    private matDialogService: MatDialog,
    private offlineService: OfflineService,
    private dataLayerService: DataLayerService,
    private deviceService: DeviceDetectorService,
    private dynamicCssService: DynamicCssService,
    private touchpointNotificationService: TouchpointNotificationService,
    private dateAdapter: DateAdapter<Date>,
    @Inject(DOCUMENT) private document: Document
  ) { }

  ngOnInit() {
    this.dataLayerService.gtmInit();
    this.dataLayerService.clarityInit();
    this.dataLayerService.formbricksInit();
    this.shouldDisplayOfflineTimer = !this.appStateService.state.isOnline;

    this.isOnline = this.appStateService.state.isOnline;
    this.isMobile = this.deviceService.isMobile();
    this.appStrings = this.appInfoService.appStrings;

    this.dateAdapter.setLocale(this.appInfoService.dateLocale);
    this.subs.add(this.appInfoService.countrySelected$.subscribe(() => {
      this.dateAdapter.setLocale(this.appInfoService.dateLocale);
    }));

    this.router.events.subscribe((triggeredEvent) => {
      if (triggeredEvent instanceof NavigationStart) {
        // close all opened dialogs
        this.matDialogService.openDialogs.forEach(dialog => {
          if (dialog.componentInstance instanceof PaymentChaseComponent) {
            return;
          }
          dialog.close();
        });
      }
    });

    this.router.events.subscribe((triggeredEvent) => {
      if (triggeredEvent instanceof GuardsCheckEnd) {
        // dynamically switch styles based on redesigned pages
        // TODO: remove this after all pages are using DS
        const route = triggeredEvent.url;
        if (triggeredEvent.shouldActivate) {
          const shouldUseDsTheme = this.shouldUseDsTheme(route);

          if (shouldUseDsTheme) {
            this.dynamicCssService.removeStyle('assets/crm-theme.css');
            this.dynamicCssService.loadStyle('assets/new-crm-theme.css');
          } else {
            this.dynamicCssService.removeStyle('assets/new-crm-theme.css');
            this.dynamicCssService.loadStyle('assets/crm-theme.css');
          }
        }
      }
    });

    if (this.isOnline) {
      // online mode, set authenticated state
      if (!this.appStateService.state.isInternetConnected) {
        this.router.navigate(['/no-internet']);
        return;
      }
      this.isAuthenticated = this.authenticationService.isAuthenticated;

      if (!this.isAuthenticated) {
        return;
      }

      this.syncOutlook();
    } else {
      // offline server is closed
      if (this.appStateService.state.localServerFailure) {
        this.router.navigate(['/no-internet']);
        return;
      }

      // offline mode, go to offline login page
      this.router.navigate(['/offline-login']);
      return;
    }

    // online mode
    // check user access
    if (this.forbiddenAccess()) {
      setTimeout(() => this.router.navigate(['/403', true]));
      return;
    }

    this.sessionService.setupSessionMonitoring(environment.sessionTimeoutSeconds, environment.idleCheckIntervalSeconds);
    this.touchpointNotificationService.start();

    this.electronStateHandle();

    this.router.events
      .subscribe(event => this.handleNavigationEvent(event));

    this.subs.add(this.appStateService.offlineStateToggle$.subscribe(newState => this.checkIfShouldDisplayOfflineTimer(newState)));
    this.checkIfShouldDisplayOfflineTimer(this.isOnline);

    if (
      this.appInfoService.userData.countrySelection &&
      !window.localStorage.getItem('selectedCountry')
    ) {
      this.router.navigate(['/country-select']);
    }

    if (this.isMobile) {
      this.dynamicCssService.loadStyle('assets/mobile.css');
    }
  }

  /**
   * Checks if the user has not access
   * @returns true when the access is forbidden
   */
  forbiddenAccess() {
    return !this.appInfoService.userHasAccess() || !this.appInfoService.userTypeHasAccessForPhaseOne();
  }

  /**
   * Handle navigation start and end events
   */
  handleNavigationEvent(event) {
    if (event instanceof NavigationStart) {
      this.checkHomePageRoute(event.url);
    }
    if (event instanceof NavigationEnd) {
      this.notificationService.dismissNotification();
    }
  }

  /**
   * Handle electron local storage
   */
  electronStateHandle() {
    // if electron
    if (this.appStateService.state.isElectron) {
      this.offlineService.isSyncStatusBusy = false;

      // if need to send sync data to the server, trigger sync
      if (window.localStorage.getItem(this.appStateService.shouldSyncKey)) {
        // should call goOnline sync
        this.offlineService.triggerStartGoOnlineSync$.next(true);
      }

      // skip start receiving updates this if goOnline is triggered
      if (!window.localStorage.getItem(this.appStateService.goOnlineTriggeredKey)) {
        // start receiving updates
        this.checkStatusAndStartReceivingOnlineUpdates();
      } else {
        window.localStorage.removeItem(this.appStateService.goOnlineTriggeredKey);
      }
    }
  }

  checkHomePageRoute(url: string) {
    if (!this.shouldCheckHomePage) {
      return;
    }
    this.shouldCheckHomePage = false;

    if (this.appInfoService.isAdminTypeUser() && url === '/') {
      this.router.navigate(['/accounts']);
    } else if (this.appInfoService.isAccountManager() && this.appInfoService.isNSLocaleSelected()) {
      this.router.navigate(['/quotes']);
    }
  }

  /**
   * Starts initial outlook sync if it is not already done - only for an AM
   */
  syncOutlook() {
    if (this.appInfoService.isAccountManager() && !sessionStorage.getItem('initialOutlookSyncStarted')) {
      this.appInfoService.startSynchOutlook().subscribe();
    }
  }

  /**
   * Sets the flag used for display offline timer by isOnline flag
   */
  private checkIfShouldDisplayOfflineTimer(newState) {
    this.shouldDisplayOfflineTimer = !newState;
  }

  /**
   * Checks previous sync status and starts initial sync
   */
  private checkStatusAndStartReceivingOnlineUpdates() {
    this.offlineService.isSyncStatusBusy = true;
    this.appInfoService.getOnlineSyncStatusTemplateValues(templateValues => {
      this.appInfoService.getLastOnlineSyncStatus()
        .subscribe(syncStatus => {
          if (templateValues.syncStatus[syncStatus.synchronizationStatus] !== this.offlineService.syncStatuses.inProgress) {
            this.offlineService.startReceivingOnlineUpdates();
          } else {
            this.offlineService.isSyncStatusBusy = false;
          }
        }, () => this.offlineService.isSyncStatusBusy = false);
    });

  }

  /**
   * Determines if a route should use the Design System theme
   * @param route The current route URL
   * @returns true if DS theme should be loaded, false for legacy theme
   */
  private shouldUseDsTheme(route: string): boolean {
    // Check exact routes (both mobile and desktop)
    const exactRoutes = [...DS_ROUTES_CONFIG.exactRoutes];
    if (this.appInfoService.isAccountManager()) {
      exactRoutes.push('/');
    }

    const isExactMatch = exactRoutes.includes(route);
    const isDesktopPatternMatch = DS_ROUTES_CONFIG.desktopPatterns.some(pattern => route.includes(pattern));

    if (this.isMobile) {
      const isMobileExactMatch = DS_ROUTES_CONFIG.mobileExactRoutes.includes(route);
      const isMobilePatternMatch = DS_ROUTES_CONFIG.mobilePatterns.some(pattern => route.includes(pattern));
      return isExactMatch || isDesktopPatternMatch || isMobileExactMatch || isMobilePatternMatch;
    }

    return isExactMatch || isDesktopPatternMatch;
  }

  ngOnDestroy() {
    this.touchpointNotificationService.stop();
    this.subs.unsubscribe();
  }
}
